import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/**
 * AuthContext — single source of truth for the current Supabase session.
 *
 * Wraps the app at the top level (in App.tsx). All other auth-aware code
 * (`ProtectedRoute`, `useCurrentCustomer`) reads from this context rather
 * than calling `supabase.auth.getSession()` directly — fewer round trips,
 * consistent state on re-render.
 *
 * The `loading` flag is true on first mount until the initial session has
 * been resolved (whether or not it exists). Components that gate routes
 * MUST wait for `loading === false` before deciding to redirect.
 *
 * If `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY` are missing, this provider
 * runs in "unauthenticated mock mode" — `loading` resolves to false, session
 * stays null, and the rest of the app falls back to mock data. This keeps
 * the dev experience working when env vars haven't been wired yet, instead
 * of crashing the whole app at the AuthProvider boundary.
 */

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  // `user` is stored separately from `session` so its reference stays STABLE
  // across token refreshes. Supabase fires onAuthStateChange with a brand-new
  // session object every time the tab regains focus (TOKEN_REFRESHED). If we
  // derived `user` from `session` directly, its identity would change on every
  // refocus — re-running every effect keyed on [user] (useStaffRole, page data
  // fetches), flipping their `loading` back on, which unmounts in-progress
  // forms and loses whatever the user had typed. Keeping `user` stable when the
  // underlying user id is unchanged prevents that churn.
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Update session (always, for a fresh token) but only swap the `user`
  // reference when the user id actually changes (sign-in / sign-out / switch).
  const applySession = useCallback((next: Session | null) => {
    setSession(next);
    setUser((prev) => (prev?.id === (next?.user?.id ?? undefined) ? prev : next?.user ?? null));
  }, []);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    // Wrap the entire setup in try/catch so a missing/invalid Supabase config
    // doesn't unmount the AuthProvider tree. The proxy in supabase.ts throws
    // synchronously when env vars are missing.
    try {
      supabase.auth
        .getSession()
        .then(({ data }) => {
          if (!mounted) return;
          applySession(data.session);
          setLoading(false);
        })
        .catch(() => {
          if (!mounted) return;
          applySession(null);
          setLoading(false);
        });

      const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
        if (!mounted) return;
        applySession(newSession);
      });
      unsubscribe = () => data.subscription.unsubscribe();
    } catch (err) {
      console.warn(
        '[AuthProvider] Supabase not configured — running in mock mode.',
        err
      );
      applySession(null);
      setLoading(false);
    }

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [applySession]);

  const signOut = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Supabase not configured — nothing to sign out from.
    }
    applySession(null);
  };

  const value: AuthContextValue = {
    session,
    user,
    loading,
    isAuthenticated: !!session,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
